import {FC, FocusEvent, useMemo} from 'react';
import {Formik, Field, Form} from 'formik';
import {Box, Typography} from '@mui/material';
import {useAppDispatch} from '@/store/hooks';
import {changeProfile, getUserInfo} from '@/store/user';
import MultipleSelect from '@/shared/components/MultipleSelect';
import {FIELD, FIELD_LABEL, PLACEHOLDER, H6, mealsSelectOptions} from '@/utils/constants';
import Input from '@/shared/components/Inputs/Input';
import {getErrorMessage, setRequiredLabel} from '@/utils/helpers';
import {
	validationSchema,
	defaultInitialValues,
	ProfileValues,
	IProfileGeneralProps,
	normalizeMeals,
} from './formConfiguration';
import {profileGeneralForm} from './ProfileGeneralForm.style';
import {toast} from 'react-toastify';

const ProfileGeneral: FC<IProfileGeneralProps> = ({user}) => {
	const formikInitialValues = useMemo(() => {
		const initialValues = user
			? {
					email: user.email,
					firstName: user.firstName,
					lastName: user.lastName,
					phoneNumber: user.phoneNumber,
					address: user.address || '',
					city: user.city || '',
					state: user.state || '',
					zip: user.zip || '',
					meals: normalizeMeals(user.meals),
			  }
			: defaultInitialValues;
		return initialValues;
	}, []);

	const dispatch = useAppDispatch();

	const handleProfileUpdate = async (event: FocusEvent<HTMLInputElement>, param: string) => {
		if (!user) return;

		const {id, email, firstName, lastName, phoneNumber, address, city, state, zip, meals} = user;
		const params = {
			id,
			email,
			firstName,
			lastName,
			phoneNumber: phoneNumber || '',
			address: address || '',
			city: city || '',
			state: state || '',
			zip: zip || '',
			meals: meals || [],
			[param]: event.target.value,
		};

		try {
			if (user[param as keyof ProfileValues] === event.target.value) return;
			await dispatch(changeProfile(params)).unwrap();
			await dispatch(getUserInfo(user.id)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	return (
		<Formik
			initialValues={formikInitialValues}
			validationSchema={validationSchema()}
			onSubmit={() => {}}
			enableReinitialize={true}
		>
			{(formik) => (
				<Box sx={profileGeneralForm.widget}>
					<Form style={{width: '100%'}}>
						<Box>
							<Box sx={profileGeneralForm.fieldContainer}>
								<Box sx={profileGeneralForm.column}>
									<Typography variant={H6} sx={profileGeneralForm.widgetTitle}>
										Contact Information
									</Typography>

									<Box sx={profileGeneralForm.doubleField}>
										<Box sx={profileGeneralForm.doubleFieldWrap}>
											<Field
												id="firstName"
												name="firstName"
												placeholder="Enter your first name"
												label={setRequiredLabel('First name')}
												component={Input}
												onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.firstName)}
											/>
										</Box>
										<Box sx={{flex: 1}}>
											<Field
												id="lastName"
												name="lastName"
												placeholder="Enter your last name"
												label={setRequiredLabel('Last name')}
												component={Input}
												onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.lastName)}
											/>
										</Box>
									</Box>

									<Field
										id={FIELD.email}
										name={FIELD.email}
										placeholder={PLACEHOLDER.email}
										label={setRequiredLabel('Email')}
										component={Input}
										onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.email)}
									/>

									<Field
										id={FIELD.phoneNumber}
										name={FIELD.phoneNumber}
										placeholder={PLACEHOLDER.phoneNumber}
										label={setRequiredLabel(FIELD_LABEL.phoneNumber)}
										component={Input}
										onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.phoneNumber)}
									/>

									<Field
										id="meals"
										name="meals"
										placeholder="Enter your meal"
										label={setRequiredLabel('Meal')}
										value={formik.values.meals}
										defaultValue="None"
										component={MultipleSelect}
										options={mealsSelectOptions}
										onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.meals)}
									/>
								</Box>

								<Box sx={profileGeneralForm.column}>
									<Typography variant={H6} sx={profileGeneralForm.widgetTitle}>
										Address
									</Typography>

									<Field
										id="address"
										name="address"
										placeholder="Enter your address"
										label="Address"
										component={Input}
										onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.address)}
									/>

									<Field
										id="city"
										name="city"
										placeholder="Enter your city"
										label="City"
										component={Input}
										onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.city)}
									/>

									<Field
										id="state"
										name="state"
										placeholder="Enter your state"
										label="State"
										component={Input}
										onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.state)}
									/>

									<Field
										id="zip"
										name="zip"
										placeholder="Enter your zip"
										label="Zip"
										component={Input}
										onBlur={(event: FocusEvent<HTMLInputElement>) => handleProfileUpdate(event, FIELD.zip)}
									/>
								</Box>
							</Box>
						</Box>
					</Form>
				</Box>
			)}
		</Formik>
	);
};

export default ProfileGeneral;
